/**
 * PDF exporter — calls the backend `POST /api/reports/_export.pdf`
 * endpoint with the locked view-state shape, downloads the Blob.
 *
 * Backend uses reportlab (already a project dep via M29) to render
 * A4 landscape with brand header + page numbers + footer. The
 * server is the right place for the heavy template work because
 * client-side PDF render with @react-pdf/renderer would blow the
 * memory budget at 10K+ rows.
 *
 * If the backend is unavailable / errors, we surface a clean error
 * to the caller and let them fall back to XLSX export.
 */

import type { ExportPayload } from "./types";

export async function exportPdf(
  payload: ExportPayload,
  filename: string,
  apiBase: string = "",
): Promise<void> {
  const body = serializeForBackend(payload);
  const res = await fetch(`${apiBase}/api/reports/_export.pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(
      `PDF export failed (${res.status}): ${detail || res.statusText}`,
    );
  }
  const blob = await res.blob();
  triggerDownload(blob, filename);
}

function serializeForBackend(payload: ExportPayload): unknown {
  return {
    title: payload.title,
    period: payload.period ?? null,
    generated_at: payload.generatedAt.toISOString(),
    generated_by: payload.generatedBy ?? null,
    columns: payload.columns.map((c) => ({
      key: c.key,
      label: c.label,
      type: c.type,
      currency: c.currency ?? null,
      decimals: c.decimals ?? null,
    })),
    rows: payload.rows.map((row) => {
      const out: Record<string, unknown> = {};
      for (const col of payload.columns) {
        const v = row[col.key];
        out[col.key] =
          v instanceof Date ? v.toISOString() : (v ?? null);
      }
      return out;
    }),
    active_filters: payload.activeFilters ?? [],
  };
}

function triggerDownload(blob: Blob, filename: string): void {
  if (typeof window === "undefined") return;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
